/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * The states and union territories an event can be held in.
 *
 * The sibling of countries.js, for the same reason and with the same shape:
 * `event.location_state` is a free varchar(80), so a typed state produced
 * "Karnataka", "karnataka", "KA" and worse in one column. The live table
 * already holds one row whose state is 84 characters of keyboard mash, which is
 * what a required free-text field eventually collects.
 *
 * STATIC, AND DELIBERATELY SO. India's states are not application data — they
 * change by Act of Parliament, roughly twice in a decade, and a lookup table
 * or an endpoint for 36 rows that nobody may edit would be machinery guarding
 * nothing. This list ships with the code, like the country list beside it.
 *
 * THE NAME IS WHAT GETS STORED, not the code. The column already holds names
 * from events created before this list existed, and switching to codes would
 * split the very data the list is here to unify. Longest name here is 44
 * characters, so the 80-char column is not at risk.
 *
 * WHY THERE ARE NO GST STATE CODES HERE. They were the obvious thing to add,
 * since the tax work needs one — but only the SUPPLIER's code is needed, and
 * that belongs on the tax rule row rather than in a 36-entry map nothing else
 * reads. Secondary sources also disagree about several of them (whether Andhra
 * Pradesh is 28 or 37, what Ladakh is), and a tax head filed under a guessed
 * code is worse than one not filed from here at all. If a full map is ever
 * needed, it wants a CA's sign-off, not a search result.
 *
 * `iso` is ISO 3166-2:IN, which is unambiguous, and is here because it is what
 * lets a legacy "KA" or "TN" in the column be recognised rather than erased.
 */

/** 28 states, then 8 union territories, each alphabetical. */
export const INDIAN_STATES = [
  { iso: 'AP', name: 'Andhra Pradesh' },
  { iso: 'AR', name: 'Arunachal Pradesh' },
  { iso: 'AS', name: 'Assam' },
  { iso: 'BR', name: 'Bihar' },
  { iso: 'CT', name: 'Chhattisgarh' },
  { iso: 'GA', name: 'Goa' },
  { iso: 'GJ', name: 'Gujarat' },
  { iso: 'HR', name: 'Haryana' },
  { iso: 'HP', name: 'Himachal Pradesh' },
  { iso: 'JH', name: 'Jharkhand' },
  { iso: 'KA', name: 'Karnataka' },
  { iso: 'KL', name: 'Kerala' },
  { iso: 'MP', name: 'Madhya Pradesh' },
  { iso: 'MH', name: 'Maharashtra' },
  { iso: 'MN', name: 'Manipur' },
  { iso: 'ML', name: 'Meghalaya' },
  { iso: 'MZ', name: 'Mizoram' },
  { iso: 'NL', name: 'Nagaland' },
  { iso: 'OR', name: 'Odisha' },
  { iso: 'PB', name: 'Punjab' },
  { iso: 'RJ', name: 'Rajasthan' },
  { iso: 'SK', name: 'Sikkim' },
  { iso: 'TN', name: 'Tamil Nadu' },
  { iso: 'TG', name: 'Telangana' },
  { iso: 'TR', name: 'Tripura' },
  { iso: 'UP', name: 'Uttar Pradesh' },
  { iso: 'UT', name: 'Uttarakhand' },
  { iso: 'WB', name: 'West Bengal' },

  // Union territories. Flagged rather than separated: the picker shows one
  // flat alphabetical list, because an organizer looking for Delhi wants
  // Delhi and should not have to know which half of a split list it lives in.
  // The flag stays because the distinction is real elsewhere -- a union
  // territory charges UTGST in place of SGST -- and that is decided from the
  // state itself, not from how this list is rendered.
  { iso: 'AN', name: 'Andaman & Nicobar Islands', isUnionTerritory: true },
  { iso: 'CH', name: 'Chandigarh', isUnionTerritory: true },
  { iso: 'DH', name: 'Dadra & Nagar Haveli and Daman & Diu', isUnionTerritory: true },
  { iso: 'DL', name: 'Delhi', isUnionTerritory: true },
  { iso: 'JK', name: 'Jammu & Kashmir', isUnionTerritory: true },
  { iso: 'LA', name: 'Ladakh', isUnionTerritory: true },
  { iso: 'LD', name: 'Lakshadweep', isUnionTerritory: true },
  { iso: 'PY', name: 'Puducherry', isUnionTerritory: true },
];

/**
 * Renamings and common misspellings, so a value already in the column is
 * recognised rather than blanked.
 *
 * This matters more than it looks, and countries.js learned it first:
 * `OrgSelect` renders a value it has no option for as BLANK. Without this, an
 * organizer opening an old event to fix a typo in the title would find the
 * state silently empty and the save blocked by a required field that looked,
 * to them, already answered.
 *
 * Only genuine mismatches belong here. Spelling variants that differ just by
 * punctuation, case, or "and" against "&" are handled by the squash in
 * `normalizeState` and are not repeated as entries.
 */
const ALIASES = {
  // Renamed states, which is the whole reason this list is not just typos.
  orissa: 'Odisha',
  uttaranchal: 'Uttarakhand',
  pondicherry: 'Puducherry',
  pondichery: 'Puducherry',

  // The merged union territory, under each of the two names it had before.
  'dadra nagar haveli': 'Dadra & Nagar Haveli and Daman & Diu',
  'daman diu': 'Dadra & Nagar Haveli and Daman & Diu',

  // Delhi, which almost nobody writes as plainly as the list does.
  'new delhi': 'Delhi',
  'nct of delhi': 'Delhi',
  'national capital territory of delhi': 'Delhi',
  'delhi ncr': 'Delhi',

  // Real spellings seen in the wild.
  tamilnadu: 'Tamil Nadu',
  chattisgarh: 'Chhattisgarh',
  chatisgarh: 'Chhattisgarh',
  telengana: 'Telangana',
  karnatka: 'Karnataka',
  maharastra: 'Maharashtra',
  // 'TS' predates the ISO code and is still what most people type.
  ts: 'Telangana',
};

/**
 * A key that ignores everything people differ on.
 *
 * Case, punctuation, spacing, and the choice between "and" and "&" — so
 * "Jammu and Kashmir", "jammu & kashmir" and "Jammu-Kashmir" all arrive at one
 * key, and the merged union territory matches whichever of its four possible
 * spellings of "and" was stored.
 */
function squash(value) {
  return String(value)
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/\band\b/g, ' ')
    .replace(/[^a-z0-9]/g, '');
}

const BY_NAME = new Map(INDIAN_STATES.map(state => [state.name.toLowerCase(), state.name]));
const BY_ISO = new Map(INDIAN_STATES.map(state => [state.iso.toLowerCase(), state.name]));
const BY_SQUASH = new Map(INDIAN_STATES.map(state => [squash(state.name), state.name]));
const ALIAS_BY_SQUASH = new Map(
  Object.entries(ALIASES).map(([key, name]) => [squash(key), name]),
);

/**
 * Maps whatever is in the column onto a value the dropdown actually has.
 *
 * Returns '' for something unrecognisable — 84 characters of keyboard mash has
 * no right answer, and inventing one would file an event's tax under a state
 * nobody chose. The caller shows the field as unanswered and says what was
 * there before, so the old value is corrected rather than silently kept or
 * silently lost.
 */
export function normalizeState(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  const exact = BY_NAME.get(raw.toLowerCase());
  if (exact) return exact;

  const iso = BY_ISO.get(raw.toLowerCase());
  if (iso) return iso;

  const key = squash(raw);
  // Aliases BEFORE the squashed names: 'ts' squashes to a key of its own and
  // must not be left to chance against a name that happens to contain it.
  return ALIAS_BY_SQUASH.get(key) || BY_SQUASH.get(key) || '';
}

/** Is this exactly one of the values the picker offers? */
export function isKnownState(value) {
  return Boolean(value) && BY_NAME.has(String(value).trim().toLowerCase());
}

/** Options in the shape OrgSelect expects. */
export const stateOptions = () =>
  INDIAN_STATES.map(({ name }) => ({ value: name, label: name }));

/**
 * The same options, split into States and Union Territories.
 *
 * NOT used by the event form, which shows one flat list — a heading an
 * organizer has to read before they can find Delhi is a step that only costs
 * time. Kept because the split is a real one under GST, where a union
 * territory charges UTGST in place of SGST, and a screen that needs to show
 * that distinction should not have to rebuild it.
 */
export const groupedStateOptions = () => [
  {
    label: 'States',
    options: INDIAN_STATES
      .filter(state => !state.isUnionTerritory)
      .map(({ name }) => ({ value: name, label: name })),
  },
  {
    label: 'Union Territories',
    options: INDIAN_STATES
      .filter(state => state.isUnionTerritory)
      .map(({ name }) => ({ value: name, label: name })),
  },
];
